import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const PARTICLES = Array.from({ length: 30 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 3 + 1,
  opacity: Math.random() * 0.4 + 0.1,
  duration: Math.random() * 8 + 4,
  delay: Math.random() * 4
}));

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WarningIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="9" x2="12" y2="13" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
    <line x1="12" y1="17" x2="12.01" y2="17" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const HeroSection = () => {
  const navigate = useNavigate();
  const [heroVisible, setHeroVisible] = useState(false);

  // Inline hero email capture state
  const [heroEmail, setHeroEmail] = useState('');
  const [heroStatus, setHeroStatus] = useState('idle'); // idle | loading | success | duplicate | invalid | valid
  const [heroErrorMsg, setHeroErrorMsg] = useState('');
  const [heroShake, setHeroShake] = useState(false);
  const [heroHasBlurred, setHeroHasBlurred] = useState(false);
  const [heroSubmittedEmail, setHeroSubmittedEmail] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const validateHeroEmail = (val) => {
    if (!val?.trim()) return 'empty';
    if (!EMAIL_REGEX?.test(val?.trim())) return 'invalid';
    return 'valid';
  };

  const handleHeroEmailChange = (e) => {
    const val = e?.target?.value;
    setHeroEmail(val);
    if (heroHasBlurred) {
      const result = validateHeroEmail(val);
      if (result === 'valid') {
        setHeroStatus('valid');
        setHeroErrorMsg('');
      } else if (result === 'invalid') {
        setHeroStatus('invalid');
        setHeroErrorMsg('Please enter a valid email address (e.g. name@example.com)');
      } else {
        setHeroStatus('idle');
        setHeroErrorMsg('');
      }
    } else {
      if (heroStatus === 'invalid') {
        setHeroStatus('idle');
        setHeroErrorMsg('');
      }
    }
  };

  const handleHeroEmailBlur = () => {
    setHeroHasBlurred(true);
    if (!heroEmail?.trim()) {
      setHeroStatus('idle');
      setHeroErrorMsg('');
      return;
    }
    const result = validateHeroEmail(heroEmail);
    if (result === 'valid') {
      setHeroStatus('valid');
      setHeroErrorMsg('');
    } else {
      setHeroStatus('invalid');
      setHeroErrorMsg('Please enter a valid email address (e.g. name@example.com)');
    }
  };

  const handleHeroEmailSubmit = async (e) => {
    e?.preventDefault();
    setHeroHasBlurred(true);
    if (!heroEmail?.trim()) {
      setHeroStatus('invalid');
      setHeroErrorMsg('Please enter your email address');
      setHeroShake(true);
      setTimeout(() => setHeroShake(false), 600);
      return;
    }
    if (!EMAIL_REGEX?.test(heroEmail?.trim())) {
      setHeroStatus('invalid');
      setHeroErrorMsg('Please enter a valid email address (e.g. name@example.com)');
      setHeroShake(true);
      setTimeout(() => setHeroShake(false), 600);
      return;
    }
    setHeroStatus('loading');
    try {
      const { error } = await supabase?.from('email_subscribers')?.insert([{ email: heroEmail?.trim()?.toLowerCase(), source: 'hero_section', is_active: true }]);
      if (error) {
        if (error?.code === '23505') {
          setHeroStatus('duplicate');
        } else {
          setHeroStatus('invalid');
          setHeroErrorMsg('Something went wrong. Please try again.');
        }
        return;
      }
      setHeroSubmittedEmail(heroEmail?.trim()?.toLowerCase());
      setHeroStatus('success');
    } catch {
      setHeroStatus('invalid');
      setHeroErrorMsg('Something went wrong. Please try again.');
    }
  };

  const getHeroBorderStyle = () => {
    if (heroStatus === 'invalid') return '1.5px solid #EF4444';
    if (heroStatus === 'valid') return '1.5px solid #22C55E';
    return '1.5px solid rgba(212,165,32,0.3)';
  };

  return (
    <section id="hero" className="relative min-h-screen bg-[#0B1F3B] flex flex-col overflow-hidden">
      {/* Particle Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {PARTICLES?.map((p) =>
        <div
          key={p?.id}
          className="absolute rounded-full bg-[#D4A520]"
          style={{
            left: `${p?.x}%`,
            top: `${p?.y}%`,
            width: `${p?.size}px`,
            height: `${p?.size}px`,
            opacity: p?.opacity,
            animation: `float ${p?.duration}s ${p?.delay}s ease-in-out infinite alternate`
          }} />

        )}
        {/* Geometric SVG Pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#D4A520" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        {/* Radial glow */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#D4A520]/5 blur-3xl" />
      </div>
      {/* Main Hero Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left: Text Content */}
            <div className={`transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* Tagline Badge */}
              <div className="inline-flex items-center gap-2 bg-[#D4A520]/15 border border-[#D4A520]/30 rounded-full px-4 py-1.5 mb-4">
                <div className="w-1.5 h-1.5 rounded-full bg-[#D4A520] animate-pulse" />
                <span className="text-[#D4A520] text-xs font-semibold tracking-widest uppercase">Invest In Each Other™</span>
              </div>

              <h1
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                
                The Premier
                <span className="block text-[#D4A520]">Investment Network</span>
                for Women
              </h1>

              <p className="text-white/70 text-base md:text-xl mb-6 leading-relaxed" style={{ fontFamily: 'Source Serif Pro, serif' }}>
                Build wealth. Connect with purpose. Invest in each other.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8">
                <button
                  onClick={() => navigate('/onboarding-authentication')}
                  className="relative w-full sm:w-auto px-8 py-3.5 text-[#0B1F3B] font-bold rounded-xl text-base overflow-hidden"
                  style={{
                    background: '#D4A520',
                    transition: 'transform 250ms ease-out, box-shadow 250ms ease-out, background 250ms ease-out',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.04)';
                    e.currentTarget.style.background = '#E8C040';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(212,165,32,0.5)';
                    const shine = e?.currentTarget?.querySelector('.btn-shine');
                    if (shine) { shine.style.transform = 'translateX(100%)'; shine.style.transition = 'transform 500ms ease'; }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.background = '#D4A520';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(212,165,32,0.2)';
                    const shine = e?.currentTarget?.querySelector('.btn-shine');
                    if (shine) { shine.style.transform = 'translateX(-100%)'; shine.style.transition = 'none'; }
                  }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1.04)'; }}
                >
                  <span className="btn-shine absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)', transform: 'translateX(-100%)' }} />
                  Join WIN Today
                </button>
                <button
                  onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                  className="relative w-full sm:w-auto px-8 py-3.5 font-semibold rounded-xl text-base overflow-hidden"
                  style={{
                    border: '2px solid #D4A520',
                    color: '#D4A520',
                    background: 'transparent',
                    transition: 'transform 250ms ease-out, box-shadow 250ms ease-out',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(11,31,59,0.3)';
                    const fill = e?.currentTarget?.querySelector('.btn-fill');
                    if (fill) { fill.style.transform = 'translateX(0)'; }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    const fill = e?.currentTarget?.querySelector('.btn-fill');
                    if (fill) { fill.style.transform = 'translateX(-100%)'; }
                  }}
                  onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
                  onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                >
                  <span className="btn-fill absolute inset-0 pointer-events-none rounded-xl" style={{ background: '#0B1F3B', transform: 'translateX(-100%)', transition: 'transform 300ms ease-out' }} />
                  <span className="relative z-10">See How It Works</span>
                </button>
              </div>

              {/* Inline Hero Email Capture */}
              <div className="border-t border-[#D4A520]/20 pt-5">
                {heroStatus === 'success' ?
                <div className="flex items-center gap-3" style={{ animation: 'fadeInUp 0.4s ease forwards' }}>
                    <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(212,165,32,0.15)', border: '1.5px solid #D4A520' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="#D4A520" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                          style={{ animation: 'drawCheck 0.45s ease 0.05s forwards', strokeDasharray: 30, strokeDashoffset: 30 }} />
                      </svg>
                    </div>
                    <div>
                      <span className="text-[#D4A520] text-sm font-semibold block">You're in! Check your inbox for a welcome gift 🎉</span>
                      <a href="/?ref=share" className="text-[#D4A520]/70 text-xs hover:text-[#D4A520] transition-colors">Share WIN with a friend →</a>
                    </div>
                  </div> :
                heroStatus === 'duplicate' ?
                <p className="text-[#D4A520]/80 text-sm" style={{ animation: 'fadeInUp 0.3s ease forwards' }}>You're already on our list! We'll see you in your inbox.</p> :

                <form onSubmit={handleHeroEmailSubmit} noValidate>
                    <p className="text-white text-xs mb-3 tracking-wide uppercase">Get free weekly insights</p>
                    <div className={`flex flex-col sm:flex-row gap-2 ${heroShake ? 'animate-shake' : ''}`}>
                      <div className="relative w-full sm:flex-1 sm:min-w-0">
                        <input
                        type="email"
                        value={heroEmail}
                        onChange={handleHeroEmailChange}
                        onBlur={handleHeroEmailBlur}
                        placeholder="your@email.com"
                        className="w-full px-4 pr-9 py-2.5 rounded-lg bg-white/10 text-white placeholder-white/40 text-sm outline-none transition-all duration-300"
                        style={{ border: getHeroBorderStyle() }}
                        onFocus={(e) => {
                          if (heroStatus !== 'invalid' && heroStatus !== 'valid') {
                            e.target.style.border = '1.5px solid #D4A520';
                            e.target.style.boxShadow = '0 0 0 2px rgba(212,165,32,0.2)';
                          }
                        }}
                        onBlurCapture={(e) => {
                          if (heroStatus !== 'invalid' && heroStatus !== 'valid') {
                            e.target.style.border = '1.5px solid rgba(212,165,32,0.3)';
                            e.target.style.boxShadow = 'none';
                          }
                        }}
                        disabled={heroStatus === 'loading'} />
                        {/* Valid checkmark */}
                        {heroStatus === 'valid' && (
                          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" fill="#22C55E" />
                              <path d="M7 12l4 4 6-7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        )}
                        {/* Invalid X icon */}
                        {heroStatus === 'invalid' && (
                          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" fill="#EF4444" />
                              <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <button
                      type="submit"
                      disabled={heroStatus === 'loading'}
                      className="w-full sm:w-auto px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 whitespace-nowrap"
                      style={{
                        border: '1.5px solid #D4A520',
                        color: '#D4A520',
                        background: 'transparent',
                        opacity: heroStatus === 'loading' ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {if (heroStatus !== 'loading') {e.currentTarget.style.background = 'rgba(212,165,32,0.15)';}}}  
                      onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent';}}>
                        {heroStatus === 'loading' ? (
                          <span className="flex items-center justify-center gap-1.5">
                            <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="#D4A520" strokeWidth="3" strokeDasharray="31.4" strokeDashoffset="10" />
                            </svg>
                            <span>Sending...</span>
                          </span>
                        ) : 'Get Free Insights'}
                      </button>
                    </div>
                    {heroStatus === 'invalid' && heroErrorMsg && (
                      <div className="flex items-center gap-1 mt-1.5" style={{ animation: 'fadeInError 0.2s ease forwards' }}>
                        <WarningIcon />
                        <p className="text-xs" style={{ color: '#EF4444' }}>{heroErrorMsg}</p>
                      </div>
                    )}
                  </form>
                }
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className={`flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="relative w-full max-w-lg">
                {/* Decorative border frame */}
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-[#D4A520]/30 via-transparent to-[#D4A520]/10 blur-sm" />
                <div className="absolute -inset-0.5 rounded-2xl border border-[#D4A520]/20" />
                <img
                  src="/assets/images/AdobeStock_1035590571-1772865770109.jpeg"
                  alt="Woman putting coin into piggy bank with investment growth chart arrows"
                  className="relative w-full h-64 sm:h-80 md:h-96 object-cover rounded-2xl shadow-2xl shadow-[#D4A520]/20" />
                
                {/* Overlay gradient at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0B1F3B]/60 to-transparent rounded-b-2xl" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <style>{`
        @keyframes float {
          0% { transform: translateY(0px) translateX(0px); }
          100% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes spin {
          from { transform: rotateY(0deg); }
          to { transform: rotateY(360deg); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes drawCheck {
          to { stroke-dashoffset: 0; }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
        .animate-shake {
          animation: shake 0.6s ease;
        }
        @keyframes fadeInError {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );

};

export default HeroSection;